import {useCallback, useMemo, useState} from 'react';

import uniqueId from 'lodash.uniqueid';
import {useTemporaryLimitActions} from 'src/components/connector-limits-form';
import {
    DISCARD_CHANGES_SUCCESS_MESSAGE,
    MAX_NUMBER_VALUE,
    SAVE_CHANGES_SUCCESS_MESSAGE,
} from 'src/constants';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {STATSIG_GATES} from 'src/contexts/statsig-contex/constants';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {getMcpLimits} from 'src/openapi/admin/apis/GetMcpLimitsApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {useGateValue} from '@pb/kit-lib-statsig';

import {useAccountMcpLimits} from './useAccountMcpLimits';
import {useAccountMcpTemporaryLimits} from './useAccountMcpTemporaryLimits';

import type {Limit} from 'src/openapi/admin/models/Limit';

const SELECT_MCP_CODE = 'MCP_CORP_M';
const SELECT_MCP_DEFAULT_VALUE = 150;

export const getDefaultMcpLimitValue = (code: string) =>
    code === SELECT_MCP_CODE
        ? SELECT_MCP_DEFAULT_VALUE
        : MAX_NUMBER_VALUE;

type Options = {
    onSaveSuccess?: () => void,
};

export const useAccountConnectorLimits = (
    accountId: number,
    {onSaveSuccess}: Options = {},
) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const {canEditAccountConnectorLimits} = useAdminAccessRight();
    const {accountFieldChange} = useEventTracking();
    const {getTodayString, today} = useDateTime();
    const [busy, setBusy] = useState(false);

    const editDisabled = useMemo(
        () => !canEditAccountConnectorLimits(accountId),
        [accountId, canEditAccountConnectorLimits],
    );
    const isPremiumMcpEnabled = useGateValue(
        STATSIG_GATES.PREMIUM_MCP_LIMITS,
    );

    const {
        data: mcpLimitsDictionary,
        isLoading: isDictLoading,
        loadingError: dictError,
    } = useReadOnlyDataHook({
        initValue: [],
        getDispatchAction: useCallback(
            (signal: AbortSignal) => apiDispatch(
                getMcpLimits({signal}),
            ),
            [apiDispatch],
        ),
    });

    const {
        data,
        setData,
        isLoading,
        loadingError,
        isChanged,
        saveChanges,
        discardChanges,
    } = useAccountMcpLimits({accountId});

    const {
        data: tempLimitsSettings,
        dataInitial: initialTempLimitsSettings,
        setData: setTempLimitsSettings,
        isLoading: isTempLoading,
        loadingError: tempLoadingError,
        isChanged: isTempChanged,
        saveChanges: saveTempLimits,
        discardChanges: discardTempLimits,
    } = useAccountMcpTemporaryLimits({accountId});

    const {
        changeTemporaryLimit,
        clearTemporaryLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    } = useTemporaryLimitActions({
        setTempLimitsSettings,
        mcpLimitsDictionary,
        trackFieldChange: accountFieldChange,
        getTodayString,
    });

    const anyChanged = isChanged || isTempChanged;
    const isLoadingAny = isLoading || isDictLoading || isTempLoading;
    const combinedError = loadingError || dictError || tempLoadingError;

    const getDefaultValue = useCallback(
        (code: string) => getDefaultMcpLimitValue(code),
        [],
    );

    const onSave = useCallback(async () => {
        if (tempLimitsSettings.expiresOn
            && !tempLimitsSettings.limits.length
            && isTempChanged) {
            onCreateNote({
                id: uniqueId('account-mcp-limits-save-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => 'At least one temporary limit'
                    + ' must be specified',
            });
            return;
        }

        setBusy(true);
        const errors: string[] = [];

        if (isChanged) {
            const error = await saveChanges();
            if (error && typeof error === 'string') {
                errors.push(error);
            }
        }

        if (isTempChanged) {
            const error = await saveTempLimits(
                initialTempLimitsSettings.limits,
            );
            if (error && typeof error === 'string') {
                errors.push(error);
            }
        }

        setBusy(false);

        if (errors.length) {
            onCreateNote({
                id: uniqueId('account-mcp-limits-save-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => errors.join('; '),
            });
        } else {
            onSaveSuccess?.();
            onCreateNote({
                id: uniqueId('account-mcp-limits-save-'),
                status: Status.SUCCESS,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => SAVE_CHANGES_SUCCESS_MESSAGE,
            });
        }
    }, [
        tempLimitsSettings, isChanged, isTempChanged,
        saveChanges, saveTempLimits, initialTempLimitsSettings,
        onCreateNote, onSaveSuccess,
    ]);

    const onDiscard = useCallback(() => {
        discardChanges();
        discardTempLimits();
        onCreateNote({
            id: uniqueId('account-mcp-limits-discard-'),
            status: Status.SUCCESS,
            buttonProps: {iconProps: {type: Icons.CLOSE}},
            autoHideTimeout: RESPONSE_NOTE_LIFETIME,
            body: () => DISCARD_CHANGES_SUCCESS_MESSAGE,
        });
    }, [discardChanges, discardTempLimits, onCreateNote]);

    const onLimitChange = useCallback(
        (limitId: string, newValue?: number) => {
            const description = mcpLimitsDictionary.find(
                d => d.code === limitId,
            )?.description ?? limitId;
            accountFieldChange({
                parameterName: description,
                section: 'Connector Limits',
                viewType: 'Existed',
            });

            const unlimited = newValue === undefined;
            const value = unlimited ? MAX_NUMBER_VALUE : newValue;

            setData(prevData => [
                ...prevData.filter(l => l.limitId !== limitId),
                {limitId, value, unlimited} as Limit,
            ]);
        },
        [setData, mcpLimitsDictionary, accountFieldChange],
    );

    const clearCustomLimit = useCallback(
        (limitId: string) => {
            const defaultValue = getDefaultMcpLimitValue(limitId);
            setData(prevData => prevData.map(
                l => l.limitId === limitId
                    ? {
                            limitId,
                            value: defaultValue,
                            unlimited: defaultValue
                                === MAX_NUMBER_VALUE,
                        } as Limit
                    : l,
            ));
        },
        [setData],
    );

    const isDefaultLimits = useMemo(
        () => data.every(
            l => l.value
                === getDefaultMcpLimitValue(l.limitId),
        ) && tempLimitsSettings.expiresOn === '',
        [data, tempLimitsSettings.expiresOn],
    );

    const onResetToDefaults = useCallback(() => {
        setData(mcpLimitsDictionary.map(d => {
            const defaultValue = getDefaultMcpLimitValue(
                d.code,
            );
            return {
                limitId: d.code,
                value: defaultValue,
                unlimited: defaultValue
                    === MAX_NUMBER_VALUE,
            } as Limit;
        }));
        setTempLimitsSettings({expiresOn: '', limits: []});
    }, [setData, setTempLimitsSettings, mcpLimitsDictionary]);

    return {
        busy,
        editDisabled,
        isPremiumMcpEnabled,
        mcpLimitsDictionary,
        data,
        tempLimitsSettings,
        anyChanged,
        isLoadingAny,
        combinedError,
        today,

        isDefaultLimits,
        getDefaultValue,
        onSave,
        onDiscard,
        onLimitChange,
        onResetToDefaults,
        changeTemporaryLimit,
        clearTemporaryLimit,
        clearCustomLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    };
};
